'use client';

import { useEffect, useRef, useState } from 'react';
import LiveButton from '@/components/Button/LiveButton';

type LiveExplainButtonProps = {
  onLiveTutor: () => void;
  onQuickExplain: () => void;
  className?: string;
  liveLabel?: string;
  quickLabel?: string;
  disabled?: boolean;
};

export default function LiveExplainButton({
  onLiveTutor,
  onQuickExplain,
  className = '',
  liveLabel = 'Live Tutor',
  quickLabel = 'Quick Explain',
  disabled = false,
}: LiveExplainButtonProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const openMenu = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const closeMenuWithDelay = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      closeTimeoutRef.current = null;
    }, 120);
  };

  const handleLiveTutor = () => {
    if (disabled) return;
    setIsOpen(false);
    onLiveTutor();
  };

  const handleQuickExplain = () => {
    if (disabled) return;
    setIsOpen(false);
    onQuickExplain();
  };

  return (
    <div
      ref={rootRef}
      className={`relative inline-flex items-center overflow-visible ${className}`}
      onMouseEnter={openMenu}
      onMouseLeave={closeMenuWithDelay}
    >
      <div
        aria-hidden={!isOpen}
        className={`absolute right-full top-1/2 mr-3 -translate-y-1/2 transition-all duration-250 ease-out ${
          isOpen ? 'translate-x-0 opacity-100' : 'pointer-events-none translate-x-2 opacity-0'
        }`}
      >
        <div className="flex items-center gap-2 rounded-full border border-[#DDE8D7] bg-white/96 p-2 shadow-[0_16px_30px_rgba(76,99,55,0.14)] backdrop-blur-sm">
          <button
            type="button"
            onClick={handleQuickExplain}
            disabled={disabled}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-full border border-[#D9E4D0] bg-white px-5 py-2 text-base font-semibold text-[#6F5548] transition-colors hover:bg-[#F6FAF2] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {quickLabel}
          </button>
          <LiveButton
            ariaLabel="Live"
            label="Live"
            size="compact"
            onClick={handleLiveTutor}
            disabled={disabled}
          />
        </div>
      </div>
      <LiveButton
        ariaLabel="Choose between Live Tutor and Quick Explain"
        ariaExpanded={isOpen}
        label={liveLabel}
        variant="outline"
        onClick={() => {
          if (disabled) return;
          setIsOpen((current) => !current);
        }}
        disabled={disabled}
        className="transition-colors duration-200"
      />
    </div>
  );
}
